import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/Spinner';

const buttonVariants = cva(
    'inline-flex flex-row items-center justify-center gap-2 flex-shrink-0 px-2 text-[14px] leading-5 font-medium transition-colors relative disabled:cursor-not-allowed',
    {
        variants: {
            variant: {
                primary:
                    'bg-white-100 text-grey-700 hover:bg-grey-25 disabled:bg-grey-550 disabled:text-grey-300',
                secondary:
                    'bg-grey-500 text-white-100 hover:bg-grey-550 disabled:bg-grey-550 disabled:text-grey-300',
                danger: 'bg-danger-500 text-white-100 hover:bg-danger-600 disabled:bg-danger-900 disabled:text-white-100/50',
                ghost: 'text-grey-200 hover:bg-surface-hover disabled:text-grey-400',
                ghostIdle:
                    'text-white-100 hover:bg-surface-hover disabled:text-grey-400',
                tertiary:
                    'border border-grey-500 bg-transparent text-grey-100 hover:text-white-100 disabled:text-grey-400 disabled:border-grey-500',
                pill: 'rounded-full border border-grey-600 text-grey-200 pl-[0.9rem] pr-[0.9rem] hover:bg-grey-600 hover:border-grey-600 hover:text-white-100 disabled:opacity-50 disabled:border-grey-600 disabled:bg-transparent',
                outline:
                    'border border-brand bg-brand/40 text-white-100 hover:bg-brand/60 disabled:bg-grey-550 disabled:text-grey-300 disabled:border-grey-550',
            },
            size: {
                icon: 'h-4 w-4 p-0 min-w-0 shrink-0',
                small: 'h-7',
                medium: 'h-8',
                large: 'h-10',
            },
            shape: {
                square: 'rounded-[5px]',
                round: 'rounded-[16px]',
                none: '',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'medium',
            shape: 'square',
        },
    },
);

export type ButtonVariant = VariantProps<typeof buttonVariants>['variant'];
export type ButtonSize = VariantProps<typeof buttonVariants>['size'];
export type ButtonShape = VariantProps<typeof buttonVariants>['shape'];

export interface ButtonProps
    extends
        React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant = 'primary',
            size = 'medium',
            shape = 'square',
            className,
            children,
            leadingIcon,
            trailingIcon,
            disabled,
            loading = false,
            ...props
        },
        ref,
    ) => {
        const isDisabled = disabled || loading;
        const spinnerVariant = variant === 'primary' ? 'dark' : 'light';

        return (
            <button
                ref={ref}
                className={cn(
                    buttonVariants({
                        variant,
                        size,
                        shape: variant === 'pill' ? 'none' : shape,
                    }),
                    loading && variant === 'primary' && 'disabled:bg-white-100',
                    loading && variant === 'danger' && 'disabled:bg-danger-500',
                    loading &&
                        (size === 'medium'
                            ? 'min-w-8'
                            : size === 'small'
                              ? 'min-w-7'
                              : 'min-w-10'),
                    className,
                )}
                disabled={isDisabled}
                {...props}
            >
                {loading && (
                    <span className="absolute inset-0 flex items-center justify-center px-2">
                        <Spinner variant={spinnerVariant} size={16} />
                    </span>
                )}
                <span
                    className={cn(
                        'flex items-center gap-1.5 text-[14px] leading-5',
                        loading && 'invisible',
                    )}
                >
                    {leadingIcon}
                    {children && <span>{children}</span>}
                    {trailingIcon != null ? (
                        <span
                            className={cn(
                                'inline-flex items-center',
                                variant === 'primary' && !isDisabled
                                    ? '[&_kbd]:bg-grey-50 [&_kbd]:text-grey-200'
                                    : '[&_kbd]:bg-grey-500 [&_kbd]:text-grey-300',
                            )}
                        >
                            {trailingIcon}
                        </span>
                    ) : null}
                </span>
            </button>
        );
    },
);

Button.displayName = 'Button';
